/**
 * Opening the microphone, for voice and for "Test microphone". The chosen
 * device may be gone (an unplugged headset): then the system default is
 * opened instead of none, and the caller is told so it can say it. Live
 * bug 2026-10-28: a saved device that no longer existed left voice
 * "listening only" with three working microphones present, and the test
 * blamed the browser's permission.
 */

/** Why no microphone could be opened. */
export type MicFailure =
  /** The browser or the person refused the permission. */
  | 'denied'
  /** There is no microphone at all. */
  | 'none'
  /** It exists but couldn't start (in use elsewhere, a driver error). */
  | 'failed';

export class MicOpenError extends Error {
  constructor(readonly failure: MicFailure) {
    super(`microphone: ${failure}`);
    this.name = 'MicOpenError';
  }
}

export interface OpenedMic {
  stream: MediaStream;
  /** True when the chosen device was missing and the system default was opened instead. */
  fellBack: boolean;
}

function failureOf(e: unknown): MicFailure {
  const name = (e as { name?: string } | null)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'none';
  return 'failed';
}

/** A chosen device that is not there any more (Chrome: OverconstrainedError on deviceId). */
function missing(e: unknown): boolean {
  return failureOf(e) === 'none';
}

export async function openMicStream(
  deviceId: string | null | undefined,
  extra: MediaTrackConstraints = {},
): Promise<OpenedMic> {
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
  if (!md?.getUserMedia) throw new MicOpenError('none');
  const audio: MediaTrackConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    ...extra,
  };
  if (deviceId) {
    try {
      const stream = await md.getUserMedia({ audio: { ...audio, deviceId: { exact: deviceId } } });
      return { stream, fellBack: false };
    } catch (e) {
      if (!missing(e)) throw new MicOpenError(failureOf(e));
    }
  }
  try {
    return { stream: await md.getUserMedia({ audio }), fellBack: !!deviceId };
  } catch (e) {
    throw new MicOpenError(failureOf(e));
  }
}
